import { documentAttachmentsPath, t, type DocumentAttachmentKind, type DocumentAttachmentsAnswer } from '@bms2/shared';
import { LoadFailure } from '../../components/composites/LoadFailure';
import { DocumentFilesPanel } from './DocumentFilesPanel';
import { useApi } from './api';
import type { ReleaseCompanies } from './releaseFiles';
import styles from './DocumentPage.module.css';

/**
 * 🔒 The Files block of a document whose page answer carries no files of its
 * own (piece 22; decision 0457, T1071): the Payment, the Purchase Order, the
 * Credit Note, the Change Order and the RFQ. It reads the files through the one
 * document files read and draws the same panel the Quote, the Sales Order, the
 * Invoice and the Bill draw.
 *
 * 🔒 A remittance the accounting mailbox filed against a Payment is a file
 * tagged to that Payment (decision 1515), so it is listed here. The same read
 * carries the messages tagged to the document, drawn as From mail (decision 1663).
 *
 * 🔒 It hands the document's own write refusal to the panel, which offers the
 * upload exactly when it is null (decision 0687; T2212).
 */
export function DocumentFilesSection({ recordType, recordId, writeRefusal, apiBaseUrl = '', releaseTo = null }: {
  recordType: DocumentAttachmentKind;
  recordId: string;
  writeRefusal: string | null;
  apiBaseUrl?: string;
  /** 🔒 Decision 11480: the companies a Purchase Order's file is released to, its vendor; absent, no row offers Release. */
  releaseTo?: ReleaseCompanies | null;
}) {
  const read = useApi<DocumentAttachmentsAnswer>(documentAttachmentsPath(recordType, recordId, true), apiBaseUrl);
  // 🔨 A refused read draws nothing rather than a failure, as the journal tile beside it does.
  if (read.denied) return null;
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  return (
    <section className={styles.block} data-document-files-section={recordType}>
      <h2 className={styles.heading}>{t('documentFiles.title')}</h2>
      <LoadFailure state={read.state} onRetry={read.reload} />
      {answer ? (
        <DocumentFilesPanel recordType={recordType} recordId={recordId} files={answer.files ?? []} mail={answer.mail ?? []} apiBaseUrl={apiBaseUrl} onChanged={read.reload} writeRefusal={writeRefusal} releaseTo={releaseTo} />
      ) : null}
    </section>
  );
}
